/**
 * Share Link System
 *
 * Generate and manage shareable links for drafts with permissions and analytics
 */

import { v4 as uuidv4 } from 'uuid';

export const SHARE_PERMISSIONS = {
  VIEW: 'view', // Only permission level - simple and secure
};

export class ShareLink {
  constructor(data = {}) {
    this.shareToken = data.shareToken || this.generateToken();
    this.draftId = data.draftId;
    this.createdBy = data.createdBy;
    this.createdByName = data.createdByName;
    this.createdAt = data.createdAt || Date.now();
    this.expiresAt = data.expiresAt || null; // null = never expires
    this.permission = data.permission || SHARE_PERMISSIONS.VIEW;
    this.password = data.password || null;
    this.allowedEmails = data.allowedEmails || []; // Empty = anyone with link
    this.isActive = data.isActive !== undefined ? data.isActive : true;
    this.title = data.title || 'Untitled Draft';

    // Analytics
    this.viewCount = data.viewCount || 0;
    this.uniqueViewers = data.uniqueViewers || [];
    this.lastAccessedAt = data.lastAccessedAt || null;
    this.accessLog = data.accessLog || []; // { userId, userName, timestamp, ip }
  }

  generateToken() {
    // Generate a secure random token
    // Use shorter format for cleaner URLs: abc123def456
    return uuidv4().replace(/-/g, '').substring(0, 12);
  }

  getShareUrl(baseUrl = 'https://ygdraft.com') {
    return `${baseUrl}/shared/${this.shareToken}`;
  }

  isExpired() {
    if (!this.expiresAt) return false;
    return Date.now() > this.expiresAt;
  }

  canAccess(email = null) {
    if (!this.isActive) return false;
    if (this.isExpired()) return false;
    if (this.allowedEmails.length === 0) return true;
    if (!email) return false;
    return this.allowedEmails.includes(email);
  }

  recordAccess(userId, userName, email, ip) {
    this.viewCount += 1;
    this.lastAccessedAt = Date.now();

    // Track unique viewers
    if (userId && !this.uniqueViewers.includes(userId)) {
      this.uniqueViewers.push(userId);
    }

    // Add to access log
    this.accessLog.push({
      userId,
      userName,
      email,
      ip,
      timestamp: Date.now(),
    });

    // Keep only last 100 access logs to avoid bloat
    if (this.accessLog.length > 100) {
      this.accessLog = this.accessLog.slice(-100);
    }
  }

  toJSON() {
    return {
      shareToken: this.shareToken,
      draftId: this.draftId,
      createdBy: this.createdBy,
      createdByName: this.createdByName,
      createdAt: this.createdAt,
      expiresAt: this.expiresAt,
      permission: this.permission,
      password: this.password,
      allowedEmails: this.allowedEmails,
      isActive: this.isActive,
      title: this.title,
      viewCount: this.viewCount,
      uniqueViewers: this.uniqueViewers,
      lastAccessedAt: this.lastAccessedAt,
      accessLog: this.accessLog,
    };
  }

  static fromJSON(data) {
    return new ShareLink(data);
  }
}

export class ShareLinkService {
  /**
   * Create a new share link
   */
  static createShareLink(draftId, draftTitle, userId, userName, options = {}) {
    const shareLink = new ShareLink({
      draftId,
      title: draftTitle,
      createdBy: userId,
      createdByName: userName,
      permission: options.permission || SHARE_PERMISSIONS.VIEW,
      expiresAt: options.expiresAt || null,
      password: options.password || null,
      allowedEmails: options.allowedEmails || [],
    });

    return shareLink;
  }

  /**
   * Validate access to a share link
   */
  static validateAccess(shareLink, password = null, email = null) {
    const errors = [];

    if (!shareLink.isActive) {
      errors.push('This link has been revoked');
    }

    if (shareLink.isExpired()) {
      errors.push('This link has expired');
    }

    if (shareLink.password && shareLink.password !== password) {
      errors.push('Invalid password');
    }

    if (!shareLink.canAccess(email)) {
      errors.push('Your email is not authorized to access this link');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get analytics summary for a share link
   */
  static getAnalytics(shareLink) {
    const now = Date.now();
    const oneDayAgo = now - 24 * 60 * 60 * 1000;
    const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000;

    const recentAccess = shareLink.accessLog.filter(
      log => log.timestamp > oneDayAgo
    );

    const weeklyAccess = shareLink.accessLog.filter(
      log => log.timestamp > oneWeekAgo
    );

    return {
      totalViews: shareLink.viewCount,
      uniqueViewers: shareLink.uniqueViewers.length,
      viewsLast24h: recentAccess.length,
      viewsLast7d: weeklyAccess.length,
      lastAccessed: shareLink.lastAccessedAt,
      isExpired: shareLink.isExpired(),
      isActive: shareLink.isActive,
      recentViewers: shareLink.accessLog
        .slice(-10)
        .reverse()
        .map(log => ({
          userName: log.userName || 'Anonymous',
          email: log.email,
          timestamp: log.timestamp,
        })),
    };
  }

  /**
   * Generate expiration timestamp
   */
  static getExpirationTimestamp(days) {
    if (!days || days <= 0) return null;
    return Date.now() + days * 24 * 60 * 60 * 1000;
  }

  /**
   * Revoke a share link
   */
  static revokeLink(shareLink) {
    shareLink.isActive = false;
    return shareLink;
  }

  /**
   * Reactivate a revoked link
   */
  static reactivateLink(shareLink) {
    shareLink.isActive = true;
    return shareLink;
  }

  /**
   * Update share link settings
   */
  static updateSettings(shareLink, updates) {
    if (updates.permission !== undefined) {
      shareLink.permission = updates.permission;
    }
    if (updates.expiresAt !== undefined) {
      shareLink.expiresAt = updates.expiresAt;
    }
    if (updates.password !== undefined) {
      shareLink.password = updates.password;
    }
    if (updates.allowedEmails !== undefined) {
      shareLink.allowedEmails = updates.allowedEmails;
    }
    return shareLink;
  }
}

export default ShareLinkService;
